import React, { useState, useEffect } from 'react';

interface DatabaseManagerProps {
  onSelectDatabase: (dbName: string) => void;
}

const DatabaseManager: React.FC<DatabaseManagerProps> = ({ onSelectDatabase }) => {
  const [sqliteFiles, setSqliteFiles] = useState<string[]>([]);
  const [selectedSqlite, setSelectedSqlite] = useState<string>('');
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);

  const fetchDatabases = async () => {
    try {
      const response = await fetch('/databases/list');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSqliteFiles(data.sqlite_files || []);
    } catch (error) {
      console.error("Failed to fetch databases:", error);
      setSqliteFiles([]);
    }
  };

  useEffect(() => {
    fetchDatabases();
  }, []);

  useEffect(() => {
    if (sqliteFiles.length === 1) {
      setSelectedSqlite(sqliteFiles[0]);
    }
  }, [sqliteFiles]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const pollTaskStatus = async (taskId: string) => {
    const interval = 1000; // Poll every 1 second
    let isPolling = true;

    const doPoll = async () => {
      if (!isPolling) return; // Stop if flag is set

      try {
        const response = await fetch(`/tasks/${taskId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const task = await response.json();

        if (task.status === 'in_progress') {
          const progressPercent = Math.round(task.progress * 100);
          setUploadStatus(`正在转换MDB文件... (${progressPercent}%) - 任务ID: ${taskId}`);
          setTimeout(doPoll, interval);
        } else if (task.status === 'completed') {
          setUploadStatus(`MDB文件转换成功！`);
          fetchDatabases(); // Refresh the list
          isPolling = false; // Stop polling
        } else if (task.status === 'failed') {
          setUploadStatus(`MDB文件转换失败: ${task.error || '未知错误'}`);
          console.error("Task failed:", task.error);
          isPolling = false; // Stop polling
        } else {
          // Handle other statuses like 'pending' if necessary
          setUploadStatus(`任务状态: ${task.status} - 任务ID: ${taskId}`);
          setTimeout(doPoll, interval);
        }
      } catch (error) {
        setUploadStatus(`轮询任务状态失败: ${error}`);
        console.error("Polling error:", error);
        isPolling = false; // Stop polling on error
      }
    };

    doPoll(); // Initial call
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      alert('请选择一个MDB文件进行上传。');
      return;
    }
    setUploadStatus(`正在上传 ${file.name}... (0%)`);
    const formData = new FormData();
    formData.append('mdb_file', file);

    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setUploadStatus(`正在上传 ${file.name}... (${percent}%)`);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        setUploadStatus(`文件上传成功，开始转换 (任务ID: ${data.task_id})。`);
        pollTaskStatus(data.task_id); // Start polling
      } else {
        try {
          const errorData = JSON.parse(xhr.responseText);
          setUploadStatus(`启动 ${file.name} 转换失败: ${errorData.detail || xhr.statusText}`);
          console.error("Upload error:", errorData);
        } catch (parseError) {
          setUploadStatus(`启动 ${file.name} 转换失败: ${xhr.statusText || "未知错误"}`);
          console.error("Upload error:", xhr.statusText, xhr.responseText);
        }
      }
    };

    xhr.onerror = () => {
      setUploadStatus(`启动 ${file.name} 转换失败。网络错误。`);
      console.error("Upload error: Network error");
    };

    xhr.open('POST', '/databases/upload_mdb');
    xhr.send(formData);
  };

  const handleRemoveDatabase = async () => {
    if (!selectedSqlite) {
      alert('请选择一个要删除的数据库。');
      return;
    }
    if (!window.confirm(`确定要删除数据库 "${selectedSqlite}" 吗？此操作不可撤销。`)) {
      return;
    }

    try {
      setUploadStatus(`正在删除 ${selectedSqlite}...`);
      const response = await fetch(`/databases/delete/${selectedSqlite}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      setUploadStatus(`数据库 ${selectedSqlite} 已成功删除。正在刷新列表...`);
      setSelectedSqlite(''); // Clear selection
      fetchDatabases(); // Refresh list
    } catch (error: unknown) {
      console.error("Delete database error:", error);
      if (error instanceof Error) {
        setUploadStatus(`删除数据库失败: ${error.message}`);
      } else {
        setUploadStatus(`删除数据库失败: ${String(error)}`);
      }
    }
  };

  const handleSelect = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSqlite) {
      onSelectDatabase(selectedSqlite);
    } else {
      alert('请选择一个数据库。');
    }
  };

  return (
    <div>
      <h2>数据库管理</h2>
      <div style={{ display: 'flex', justifyContent: 'space-between', textAlign: 'center' }}>
        <div style={{ flex: 1, marginRight: '10px' }}>
          <h3>下载更新mdb</h3>
          <a href="https://www.itu.int/sns/wic/demowic25.html" target="_blank" rel="noopener noreferrer">
            查询电联网站
          </a>
        </div>
        <div style={{ flex: 1, marginRight: '10px', marginLeft: '10px' }}>
          <h3>上传MDB</h3>
          <form onSubmit={handleUpload}>
            <input type="file" accept=".mdb" onChange={handleFileChange} />
            <br />
            <button type="submit" style={{ marginTop: '10px' }}>上传</button>
            {uploadStatus && <p>{uploadStatus}</p>}
          </form>
        </div>
        <div style={{ flex: 1, marginLeft: '10px' }}>
          <h3>选择数据库</h3>
          <form onSubmit={handleSelect}>
            <select value={selectedSqlite} onChange={(e) => setSelectedSqlite(e.target.value)} size={5} style={{ width: '100%' }}>
              {sqliteFiles.map(file => <option key={file} value={file}>{file}</option>)}
            </select>
            <br />
            <button type="submit" style={{ marginTop: '10px' }}>使用选定数据库</button>
            <button type="button" onClick={handleRemoveDatabase} style={{ marginTop: '10px', marginLeft: '10px' }} disabled={!selectedSqlite}>
              删除选定数据库
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};


export default DatabaseManager;
